import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, Inbox, Mail, Phone, MapPin, X } from "lucide-react";
import { adminMe, fetchEnquiries } from "../lib/api";

const LABELS = {
  full_name: "Name", title: "Title", organization: "Organization", contact_person: "Contact Person",
  mobile: "Mobile", email: "Email", destination: "Destination", travel_date: "Travel Date",
  duration: "Duration", adults: "Adults", children: "Children", child_ages: "Child Ages",
  occasion: "Occasion", hotel_category: "Hotel Category", contact_method: "Contact Method",
  special_requests: "Special Requests", travel_category: "Category",
  total_travellers: "Total Travellers", accommodation: "Accommodation", message: "Message",
};
const HIDDEN = new Set(["id", "type", "_id"]);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || "—"; }
};
const val = (v) => (Array.isArray(v) ? (v.length ? v.join(", ") : "—") : (v || "—"));

export default function AdminInbox() {
  const nav = useNavigate();
  const token = localStorage.getItem("e2e_admin_token");
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem("e2e_admin_token");
    localStorage.removeItem("e2e_admin_user");
    nav("/admin/login");
  }, [nav]);

  useEffect(() => {
    if (!token) { nav("/admin/login"); return; }
    (async () => {
      try {
        await adminMe(token);
        const data = await fetchEnquiries(token);
        setItems(data);
      } catch (err) {
        if (err.response?.status === 401) logout();
        else setError("Failed to load enquiries.");
      }
    })();
  }, [token, nav, logout]);

  const counts = useMemo(() => {
    const list = items || [];
    return { all: list.length, prive: list.filter((i) => i.type === "prive").length, collective: list.filter((i) => i.type === "collective").length };
  }, [items]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (tab !== "all") list = list.filter((i) => i.type === tab);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((i) =>
        [i.full_name, i.organization, i.contact_person, i.email, i.destination, i.mobile]
          .filter(Boolean).some((f) => String(f).toLowerCase().includes(s)));
    }
    return list;
  }, [items, tab, q]);

  const nameOf = (i) => i.type === "prive" ? `${i.title ? i.title + " " : ""}${i.full_name}` : i.organization;

  return (
    <main data-testid="admin-inbox" className="min-h-screen bg-ivory">
      {/* Header */}
      <header className="bg-navy grain sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-gold"><Inbox size={16} /></span>
            <div>
              <p className="overline text-gold leading-none">End to End Destinations</p>
              <h1 className="font-serif text-2xl text-ivory">Enquiry Inbox</h1>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-ghost text-ivory text-sm">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Stats + tabs */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {[["all", "All", counts.all], ["prive", "Privé", counts.prive], ["collective", "Collective", counts.collective]].map(([key, label, n]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`px-4 py-2 text-sm rounded-full border transition-colors ${tab === key ? "bg-navy text-ivory border-navy" : "border-navy/20 text-charcoal/70 hover:border-gold hover:text-gold"}`}>
              {label} <span className="opacity-60">({n})</span>
            </button>
          ))}
          <div className="ml-auto relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search"
              placeholder="Search name, email, destination…"
              className="pl-9 pr-4 py-2 text-sm border border-navy/20 focus:border-gold outline-none bg-white w-72 max-w-full" />
          </div>
        </div>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        {items === null ? (
          <p className="text-charcoal/50 py-20 text-center">Loading enquiries…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-empty" className="text-charcoal/50 py-20 text-center">No enquiries yet.</p>
        ) : (
          <div className="border border-navy/10 bg-white overflow-hidden">
            <table className="w-full text-left" data-testid="admin-enquiry-table">
              <thead className="bg-navy/5">
                <tr className="text-charcoal/60 text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">Received</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Name / Organization</th>
                  <th className="px-5 py-3">Destination</th>
                  <th className="px-5 py-3 hidden md:table-cell">Contact</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i, idx) => (
                  <tr key={i.id || idx} onClick={() => setSelected(i)} data-testid={`admin-row-${idx}`}
                    className="border-t border-navy/8 hover:bg-gold/5 cursor-pointer transition-colors">
                    <td className="px-5 py-4 text-sm text-charcoal/70 whitespace-nowrap">{fmtDate(i.created_at)}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2.5 py-1 rounded-full ${i.type === "prive" ? "bg-gold/15 text-gold-700" : "bg-navy/10 text-navy"}`} style={{ color: i.type === "prive" ? "#9a7d1f" : "#0D1038" }}>
                        {i.type === "prive" ? "Privé" : "Collective"}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-serif text-lg text-navy">{nameOf(i)}</td>
                    <td className="px-5 py-4 text-charcoal/80">{i.destination || "—"}</td>
                    <td className="px-5 py-4 text-sm text-charcoal/60 hidden md:table-cell">{i.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-detail">
          <div className="absolute inset-0 bg-navy/60" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-ivory h-full overflow-y-auto shadow-2xl">
            <div className="bg-navy grain px-8 py-6 sticky top-0 flex items-start justify-between">
              <div>
                <p className="overline text-gold">{selected.type === "prive" ? "Privé Bespoke Enquiry" : "Collective Group Enquiry"}</p>
                <h2 className="font-serif text-3xl text-ivory mt-1">{nameOf(selected)}</h2>
                <p className="text-ivory/50 text-sm mt-2">{fmtDate(selected.created_at)}</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="admin-detail-close" className="text-ivory/70 hover:text-gold"><X size={22} /></button>
            </div>
            <div className="px-8 py-4 flex flex-wrap gap-3 border-b border-navy/10">
              <a href={`mailto:${selected.email}`} className="btn-gold text-sm"><Mail size={14} /> Email</a>
              <a href={`tel:${selected.mobile}`} className="btn-ghost text-navy text-sm"><Phone size={14} /> Call</a>
            </div>
            <div className="px-8 py-6 space-y-4">
              {Object.entries(selected).filter(([k, v]) => !HIDDEN.has(k) && v && !(Array.isArray(v) && v.length === 0) && k !== "created_at").map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-4 border-b border-navy/8 pb-3">
                  <p className="overline text-charcoal/50 col-span-1">{LABELS[k] || k}</p>
                  <p className="text-navy col-span-2 text-[15px] leading-relaxed break-words">
                    {k === "destination" ? <span className="inline-flex items-center gap-1"><MapPin size={13} className="text-gold" /> {val(v)}</span> : val(v)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
